<template>
  <div class="join-page">
    <div class="join-shell">
      <div v-if="loading" class="text-center text-muted py-5">Loading…</div>

      <div v-else-if="notFound" class="join-card text-center py-5">
        <i class="fe fe-link-2" style="font-size: 2.5rem; opacity: .3;"></i>
        <h1 class="h5 mt-3">This invitation link is not valid</h1>
        <p class="text-muted mb-0">Ask the agency for a new link.</p>
      </div>

      <template v-else>
        <!-- Invitation -->
        <header class="join-hero">
          <img v-if="invite.agency && invite.agency.logo_url" :src="invite.agency.logo_url" :alt="agencyName + ' logo'"
            class="join-logo" @error="$event.target.style.display = 'none'" />
          <p class="join-eyebrow">Virtual Practice Network<span v-if="invite.agency && invite.agency.state"> · {{ invite.agency.state }}</span></p>
          <h1 class="join-title">{{ agencyName }} is inviting you to join our Virtual Practice Network</h1>
          <p class="join-sub">
            Consult enrollees by video, record encounters and refer patients for tests and specialist care, from wherever you practise.
          </p>
          <p v-if="invite.message" class="join-message">{{ invite.message }}</p>
        </header>

        <div v-if="!invite.open" class="join-card text-center py-4">
          <i class="fe fe-lock" style="font-size: 2rem; opacity: .4;"></i>
          <p class="mt-2 mb-0">{{ invite.closed_reason }}</p>
        </div>

        <div v-else-if="done" class="join-card text-center py-5">
          <i class="fe fe-check-circle text-success" style="font-size: 3rem;"></i>
          <h2 class="h5 mt-3">Thank you, Dr {{ form.lastname }}</h2>
          <p class="text-muted">{{ doneMessage }}</p>
          <router-link to="/" class="btn join-btn">Go to sign in</router-link>
        </div>

        <!-- Registration -->
        <form v-else class="join-card" @submit.prevent="submit" novalidate>
          <h2 class="join-section">Your details</h2>
          <div class="form-row">
            <div class="form-group col-md-6">
              <label for="jt-first" class="join-label">First name *</label>
              <input id="jt-first" v-model="form.firstname" class="form-control" required autocomplete="given-name" />
            </div>
            <div class="form-group col-md-6">
              <label for="jt-last" class="join-label">Last name *</label>
              <input id="jt-last" v-model="form.lastname" class="form-control" required autocomplete="family-name" />
            </div>
            <div class="form-group col-md-6">
              <label for="jt-email" class="join-label">Email (used to sign in) *</label>
              <input id="jt-email" v-model="form.email" type="email" class="form-control" required autocomplete="email" />
            </div>
            <div class="form-group col-md-6">
              <label for="jt-phone" class="join-label">Phone number (11 digits) *</label>
              <input v-phone id="jt-phone" v-model="form.phone_number" class="form-control" required autocomplete="tel" />
            </div>
            <div class="form-group col-md-6">
              <label for="jt-practice" class="join-label">Practice name <span class="text-muted small">(optional)</span></label>
              <input id="jt-practice" v-model="form.practice_name" class="form-control" :placeholder="'Dr ' + (form.firstname || 'First') + ' ' + (form.lastname || 'Last')" />
            </div>
            <div class="form-group col-md-6">
              <label for="jt-license" class="join-label">MDCN registration (folio) number *</label>
              <input id="jt-license" v-model="form.license_number" class="form-control" required />
            </div>
            <div class="form-group col-md-4">
              <label for="jt-years" class="join-label">Years of practice</label>
              <input id="jt-years" v-model.number="form.years_of_experience" type="number" min="0" max="70" class="form-control" />
            </div>
            <div class="form-group col-md-8">
              <label for="jt-spec" class="join-label">Specializations * <span class="text-muted small">(select all that apply)</span></label>
              <v-select v-model="form.specialization_ids" input-id="jt-spec" :options="specializations" label="name"
                :reduce="s => s.id" multiple placeholder="Search specializations…">
                <template #option="option">{{ option.name }} <span class="text-muted small">· {{ option.category }}</span></template>
              </v-select>
            </div>
            <div class="form-group col-12">
              <label for="jt-bio" class="join-label">Short profile</label>
              <textarea id="jt-bio" v-model="form.bio" rows="3" class="form-control"
                placeholder="Training, areas of interest, languages you consult in…"></textarea>
            </div>
          </div>

          <h2 class="join-section mt-2">Qualifications <span class="text-muted small font-weight-normal">(optional, for documentation)</span></h2>
          <p class="small text-muted">MBBS certificate, MDCN licence, specialist fellowship… PDF, JPG or PNG, up to 5 MB each, at most 5 files.</p>
          <div class="doc-row" v-for="(doc, i) in documents" :key="doc.key">
            <i class="fe fe-file-text mr-2 text-muted"></i>
            <span class="doc-file text-truncate">{{ doc.file.name }}</span>
            <label :for="'jt-docname-' + doc.key" class="sr-only">Document name</label>
            <input :id="'jt-docname-' + doc.key" v-model="doc.name" class="form-control form-control-sm" placeholder="e.g. MBBS certificate" />
            <button type="button" class="btn btn-sm btn-link text-danger" :aria-label="'Remove ' + doc.file.name" @click="documents.splice(i, 1)">
              <i class="fe fe-x"></i>
            </button>
          </div>
          <label v-if="documents.length < 5" for="jt-files" class="btn btn-sm btn-outline-secondary mb-3">
            <i class="fe fe-upload mr-1"></i>Add files
          </label>
          <input id="jt-files" ref="files" type="file" class="d-none" multiple accept=".pdf,.jpg,.jpeg,.png" @change="addFiles" />

          <h2 class="join-section mt-2">Consultation hours *</h2>
          <p class="small text-muted mb-2">
            When you can take video consultations each week. Add at least one period. Patients are only booked into these times; you can change them later after signing in.
          </p>
          <div v-for="(row, i) in hours" :key="row.key" class="form-row align-items-end hours-row">
            <div class="form-group col-6 col-md-3">
              <label :for="'jt-day-' + row.key" class="join-label small">Day</label>
              <select :id="'jt-day-' + row.key" v-model.number="row.weekday" class="form-control form-control-sm">
                <option v-for="d in days" :key="d.value" :value="d.value">{{ d.label }}</option>
              </select>
            </div>
            <div class="form-group col-6 col-md-2">
              <label :for="'jt-from-' + row.key" class="join-label small">From</label>
              <input :id="'jt-from-' + row.key" v-model="row.start_time" type="time" class="form-control form-control-sm" required />
            </div>
            <div class="form-group col-6 col-md-2">
              <label :for="'jt-to-' + row.key" class="join-label small">To</label>
              <input :id="'jt-to-' + row.key" v-model="row.end_time" type="time" class="form-control form-control-sm" required />
            </div>
            <div class="form-group col-6 col-md-3">
              <label :for="'jt-slot-' + row.key" class="join-label small">Each consultation</label>
              <select :id="'jt-slot-' + row.key" v-model.number="row.slot_minutes" class="form-control form-control-sm">
                <option v-for="m in [10, 15, 20, 30, 45, 60]" :key="m" :value="m">{{ m }} minutes</option>
              </select>
            </div>
            <div class="form-group col-12 col-md-2">
              <button type="button" class="btn btn-sm btn-link text-danger" :aria-label="'Remove period ' + (i + 1)" @click="hours.splice(i, 1)">
                <i class="fe fe-trash-2"></i> Remove
              </button>
            </div>
          </div>
          <div v-if="!hours.length" class="alert alert-warning small py-2 mb-2">
            No periods added yet.
          </div>
          <div class="d-flex flex-wrap mb-3" style="gap: 6px;">
            <button type="button" class="btn btn-sm btn-light" @click="addHours"><i class="fe fe-plus mr-1"></i>Add a period</button>
            <button type="button" class="btn btn-sm btn-light" @click="weekdaysPreset">Weekdays 9:00–17:00</button>
          </div>

          <h2 class="join-section mt-2">Password</h2>
          <div class="form-row">
            <div class="form-group col-md-6">
              <label for="jt-pass" class="join-label">Password (8+ characters) *</label>
              <input id="jt-pass" v-model="form.password" type="password" class="form-control" required autocomplete="new-password" />
            </div>
            <div class="form-group col-md-6">
              <label for="jt-pass2" class="join-label">Confirm password *</label>
              <input id="jt-pass2" v-model="form.password_confirmation" type="password" class="form-control" required autocomplete="new-password" />
            </div>
          </div>

          <div v-if="errors.length" class="alert alert-danger small py-2">
            <div v-for="e in errors" :key="e">{{ e }}</div>
          </div>

          <button type="submit" class="btn join-btn btn-block" :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm mr-2" role="status"></span>
            Join the network
          </button>
          <p class="small text-muted text-center mt-3 mb-0">
            {{ agencyName }} reviews every registration before patients are sent to you.
          </p>
        </form>
      </template>
    </div>
  </div>
</template>

<script>
let fileKey = 0;
let hourKey = 0;
const DAYS = [
  { value: 1, label: "Monday" }, { value: 2, label: "Tuesday" }, { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" }, { value: 5, label: "Friday" }, { value: 6, label: "Saturday" }, { value: 0, label: "Sunday" },
];

export default {
  data() {
    return {
      loading: true,
      notFound: false,
      invite: {},
      specializations: [],
      documents: [],
      days: DAYS,
      hours: [],
      saving: false,
      errors: [],
      done: false,
      doneMessage: "",
      form: {
        firstname: "", lastname: "", email: "", phone_number: "", practice_name: "", license_number: "",
        years_of_experience: "", specialization_ids: [], bio: "", password: "", password_confirmation: "",
      },
    };
  },
  computed: {
    agencyName() {
      return (this.invite.agency && this.invite.agency.name) || "The agency";
    },
  },
  created() {
    this.axios
      .get(`/api/v1/telemedicine/join/${this.$route.params.token}`)
      .then((res) => {
        this.invite = res.data;
        document.title = `Join ${this.agencyName}'s Virtual Practice Network`;
      })
      .catch(() => (this.notFound = true))
      .finally(() => (this.loading = false));
    this.axios.get("/api/v1/specializations").then((res) => (this.specializations = res.data.data || []));
  },
  methods: {
    addFiles(event) {
      const files = Array.from(event.target.files || []);
      files.slice(0, 5 - this.documents.length).forEach((file) => {
        this.documents.push({ key: ++fileKey, file, name: file.name.replace(/\.[^.]+$/, "") });
      });
      this.$refs.files.value = "";
    },
    addHours() {
      this.hours.push({ key: ++hourKey, weekday: 1, start_time: "09:00", end_time: "13:00", slot_minutes: 30 });
    },
    weekdaysPreset() {
      this.hours = [1, 2, 3, 4, 5].map((d) => ({ key: ++hourKey, weekday: d, start_time: "09:00", end_time: "17:00", slot_minutes: 30 }));
    },
    submit() {
      this.errors = [];
      if (this.form.password !== this.form.password_confirmation) {
        this.errors = ["The passwords do not match."];
        return;
      }
      if (!this.form.specialization_ids.length) {
        this.errors = ["Select at least one specialization."];
        return;
      }
      if (!this.hours.length) {
        this.errors = ["Add at least one consultation period."];
        return;
      }
      if (this.hours.some((h) => !h.start_time || !h.end_time || h.end_time <= h.start_time)) {
        this.errors = ["Each consultation period must end after it starts."];
        return;
      }
      const data = new FormData();
      Object.entries(this.form).forEach(([key, value]) => {
        if (key === "specialization_ids") value.forEach((id) => data.append("specialization_ids[]", id));
        else if (value !== "" && value !== null) data.append(key, value);
      });
      this.documents.forEach((doc, i) => {
        data.append(`documents[${i}]`, doc.file);
        data.append(`document_names[${i}]`, doc.name || "");
      });
      this.hours.forEach((h, i) => {
        ["weekday", "start_time", "end_time", "slot_minutes"].forEach((f) => data.append(`hours[${i}][${f}]`, h[f]));
      });

      this.saving = true;
      this.axios
        .post(`/api/v1/telemedicine/join/${this.$route.params.token}`, data)
        .then((res) => {
          this.done = true;
          this.doneMessage = res.data.message;
          window.scrollTo(0, 0);
        })
        .catch((err) => {
          const d = (err.response && err.response.data) || {};
          this.errors = d.errors ? Object.values(d.errors).flat() : [d.message || "Registration failed. Please try again."];
        })
        .finally(() => (this.saving = false));
    },
  },
};
</script>

<style scoped>
.join-page { min-height: 100vh; background: linear-gradient(160deg, #f5f3ff 0%, #eef2ff 60%, #f8fafc 100%); padding: 32px 16px; }
.join-shell { max-width: 820px; margin: 0 auto; }
.join-hero { text-align: center; padding: 16px 8px 24px; }
.join-logo { width: 96px; height: 96px; object-fit: contain; border-radius: 20px; background: #fff; padding: 8px; box-shadow: 0 4px 16px rgba(76,29,149,.12); margin-bottom: 16px; }
.join-eyebrow { text-transform: uppercase; letter-spacing: .08em; font-size: .75rem; font-weight: 700; color: #7c3aed; margin-bottom: 6px; }
.join-title { font-size: 1.6rem; font-weight: 800; color: #1e1b4b; line-height: 1.3; }
.join-sub { color: #475569; max-width: 620px; margin: 10px auto 0; }
.join-message { background: #fff; border-left: 4px solid #7c3aed; border-radius: 8px; padding: 12px 16px; margin: 16px auto 0; max-width: 620px; text-align: left; white-space: pre-line; color: #334155; }
.join-card { background: #fff; border-radius: 16px; padding: 24px; box-shadow: 0 4px 20px rgba(15,23,42,.06); }
.join-section { font-size: .8rem; text-transform: uppercase; letter-spacing: .06em; color: #64748b; font-weight: 700; margin-bottom: 12px; }
.join-label { font-weight: 600; font-size: .85rem; color: #374151; }
.join-btn { background: linear-gradient(135deg, #4c1d95, #7c3aed); color: #fff; border: none; border-radius: 10px; font-weight: 700; padding: 11px 20px; }
.join-btn:hover { opacity: .92; color: #fff; }
.doc-row { display: flex; align-items: center; gap: 8px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 6px 10px; margin-bottom: 8px; }
.doc-file { max-width: 220px; font-size: .85rem; }
.hours-row { background: #f8fafc; border-radius: 8px; padding: 6px 4px 0; margin: 0 0 8px; }
@media (max-width: 576px) {
  .join-title { font-size: 1.25rem; }
  .doc-row { flex-wrap: wrap; }
}
</style>
